import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, History, Loader2 } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import BurnUp from '../../../components/abnahme/statistik/BurnUp';
import ReleaseTimeline from '../../../components/abnahme/statistik/ReleaseTimeline';
import { ChartKarte, Kachel, Legende, STATUS_FARBE, prozent, tagLang } from '../../../components/abnahme/statistik/viz';
import { abnahmeApi, type ReportDto, type StoryStatus } from '../../../lib/api/abnahme';

const HEUTE = () => new Date().toISOString().slice(0, 10);

/** Fortschrittsbalken je Epic: abgenommen, bereit, in Umsetzung — der Rest ist offen. */
const BALKEN: { status: StoryStatus; label: string }[] = [
  { status: 'abgenommen', label: 'Abgenommen' },
  { status: 'bereit_zur_abnahme', label: 'Bereit zur Abnahme' },
  { status: 'in_umsetzung', label: 'In Umsetzung' },
];

/**
 * Report für die Auftraggeber (docs/abnahme-plan.md, Abschnitt 11): Stand der
 * Abnahme heute oder zu einem Stichtag — Kennzahlen, Fortschritt je Bereich,
 * Releases und die offenen Risiken mit Titel. Keine internen Details.
 */
export default function ReportPage() {
  const [stichtag, setStichtag] = useState(HEUTE());
  const [daten, setDaten] = useState<ReportDto | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  useEffect(() => {
    let aktuell = true;
    setLaedt(true);
    setFehler(null);
    abnahmeApi
      .report(stichtag === HEUTE() ? undefined : stichtag)
      .then((antwort) => aktuell && setDaten(antwort))
      .catch((e: unknown) => {
        if (!aktuell) return;
        setDaten(null);
        setFehler(e instanceof Error ? e.message : 'Der Report ließ sich nicht laden.');
      })
      .finally(() => aktuell && setLaedt(false));
    return () => {
      aktuell = false;
    };
  }, [stichtag]);

  const kopf = (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Abnahme-Report</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Stand der Abnahme für die Auftraggeber — heute oder zu einem Stichtag.
        </p>
      </div>
      <div className="space-y-1">
        <Label htmlFor="report-stichtag">Stichtag</Label>
        <Input
          id="report-stichtag"
          type="date"
          value={stichtag}
          max={HEUTE()}
          onChange={(e) => e.target.value && setStichtag(e.target.value)}
          className="w-44"
        />
      </div>
    </div>
  );

  if (laedt && !daten) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        {kopf}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Report wird erstellt …
        </div>
      </div>
    );
  }
  if (fehler || !daten) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        {kopf}
        <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">{fehler}</div>
      </div>
    );
  }

  const { kennzahlen: k, quelle } = daten;
  const gesamt = k.stories.gesamt;
  const abgenommen = k.stories.status.abgenommen ?? 0;
  const anteil = gesamt ? Math.round((1000 * abgenommen) / gesamt) / 10 : null;
  const epics = k.epics.filter((e) => e.gesamt > 0);

  return (
    <div className="viz-root mx-auto max-w-5xl space-y-6">
      {kopf}

      {quelle.art === 'schnappschuss' && (
        <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
          <History className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <span>
            Stand vom {tagLang(quelle.tag)}
            {quelle.tag !== daten.stichtag && ` (letzter festgehaltener Tag vor dem ${tagLang(daten.stichtag)})`}.
            {quelle.rekonstruiert &&
              ' Dieser Tag wurde nachträglich aus der Historie rekonstruiert — ohne Angaben zur Testabdeckung.'}
          </span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)]">
        <div className="rounded-lg border bg-card p-6">
          <div className="text-sm font-medium text-muted-foreground">Abgenommen</div>
          <div className="mt-2 text-5xl font-semibold tabular-nums">{prozent(anteil)}</div>
          <div className="mt-1 text-sm text-muted-foreground">
            {abgenommen} von {gesamt} User Stories
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full" style={{ width: `${anteil ?? 0}%`, background: STATUS_FARBE.abgenommen }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Kachel titel="Bereit zur Abnahme" wert={k.stories.status.bereit_zur_abnahme ?? 0} zusatz="fertig, wartet auf Freigabe" />
          <Kachel titel="In Umsetzung" wert={k.stories.status.in_umsetzung ?? 0} />
          <Kachel
            titel="Kriterien durch Tests belegt"
            wert={prozent(k.abdeckung?.prozent)}
            zusatz={k.abdeckung ? `${k.abdeckung.abgedeckt} von ${k.abdeckung.kriterien}` : 'für diesen Tag nicht erfasst'}
          />
          <Kachel
            titel="Offene Blocker"
            wert={k.tickets.blocker}
            zusatz={`${k.tickets.offen} offene Tickets, ${k.tickets.nachtest} im Nachtest`}
            ton={k.tickets.blocker > 0 ? 'warnung' : 'normal'}
          />
        </div>
      </div>

      <ChartKarte
        titel="Fortschritt je Bereich"
        legende={
          <Legende
            eintraege={[
              ...BALKEN.map((b) => ({ label: b.label, farbe: STATUS_FARBE[b.status] })),
              { label: 'Noch offen', farbe: 'hsl(var(--muted))' },
            ]}
          />
        }
        tabelle={{
          spalten: ['Bereich', 'Abgenommen', 'Bereit', 'In Umsetzung', 'Gesamt'],
          zeilen: epics.map((e) => [
            e.title,
            e.status.abgenommen ?? 0,
            e.status.bereit_zur_abnahme ?? 0,
            e.status.in_umsetzung ?? 0,
            e.gesamt,
          ]),
        }}
      >
        <ul className="space-y-2.5">
          {epics.map((epic) => (
            <li key={epic.key} className="grid grid-cols-[minmax(0,14rem)_minmax(0,1fr)_3.5rem] items-center gap-3 text-sm">
              <span className="truncate" title={epic.title}>
                {epic.title}
              </span>
              <div className="flex h-3 gap-0.5 overflow-hidden rounded bg-muted">
                {BALKEN.map((b) => {
                  const anzahl = epic.status[b.status] ?? 0;
                  return anzahl > 0 ? (
                    <div
                      key={b.status}
                      title={`${b.label}: ${anzahl}`}
                      style={{ width: `${(100 * anzahl) / epic.gesamt}%`, background: STATUS_FARBE[b.status] }}
                    />
                  ) : null;
                })}
              </div>
              <span className="text-right tabular-nums text-muted-foreground">
                {epic.status.abgenommen ?? 0}/{epic.gesamt}
              </span>
            </li>
          ))}
        </ul>
      </ChartKarte>

      <BurnUp snapshots={daten.snapshots} heute={quelle.art === 'aktuell' ? { tag: daten.stichtag, stories: k.stories } : undefined} titel="Verlauf" />

      <ReleaseTimeline releases={daten.releases} bis={daten.stichtag} />

      <div className="grid gap-6 md:grid-cols-2">
        <section className="space-y-3 rounded-lg border bg-card p-5">
          <h2 className="flex items-center gap-2 text-base font-medium">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Zuletzt abgenommen
          </h2>
          {daten.zuletztAbgenommen.length === 0 ? (
            <p className="text-sm text-muted-foreground">In den 14 Tagen vor dem Stichtag wurde nichts abgenommen.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {daten.zuletztAbgenommen.map((s) => (
                <li key={`${s.key}-${s.am}`}>
                  <span className="font-mono text-xs text-muted-foreground">{s.key}</span> {s.title}
                  <div className="text-xs text-muted-foreground">
                    {tagLang(s.am)}
                    {s.version && ` · Version ${s.version}`}
                    {s.name && ` · ${s.name}`}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-3 rounded-lg border bg-card p-5">
          <h2 className="flex items-center gap-2 text-base font-medium">
            <AlertTriangle className="h-4 w-4 text-red-600" /> Offene Risiken
          </h2>
          {k.blockerListe.length === 0 && k.regressionen.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine offenen Blocker und keine Regressionen.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {k.blockerListe.map((b) => (
                <li key={b.label}>
                  <span className="font-mono text-xs text-muted-foreground">{b.label}</span> {b.titel}
                  <div className="text-xs text-muted-foreground">Blocker{b.story ? ` · Story ${b.story}` : ''}</div>
                </li>
              ))}
              {k.regressionen.map((key) => (
                <li key={key}>
                  <span className="font-mono text-xs text-muted-foreground">{key}</span> Regression
                  <div className="text-xs text-muted-foreground">
                    Abgenommen, aber inzwischen rote Tests oder ein offener Blocker.
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
